import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  LoadingSpinner,
  ScrollArea,
  SelectType,
  Separator,
} from '@ais-ui/ui-core';
import {
  Job,
  ParsedReportParams,
  ReportResult,
  ReportTemplate,
  useGetReport,
} from '@ais-ui/websockets';
import { PiCaretLeftThin, PiCaretRightLight } from 'react-icons/pi';
import { ReportDelete } from './Mutations/DeleteReport';
import { ReportDetailActionsMenu } from './ReportDetailActionsMenu';
import { useMapPanelContext } from '../Map/MapPanel/MapPanelContext';
import { CENTERCOMPONENTS } from '../Map/MapPanel/CenterComponents';

import React, { useEffect, useMemo, useState } from 'react';
import { useOnMapPanelExit } from '../Map/MapPanel/useOnMapPanelExit';
import { cn, isNonEmpty, useTimeZoneContext } from '@ais-ui/utils';
import { useReportContext } from './ReportContext';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { useManageReportProcessing } from './useManageReportProcessing';
import { useGetShiplist, useShape } from '@ais-ui/api';
import { Ship } from '@ais-ui/api-spec/config';
import { getSelectLabelByValue, reportTypesObject } from './reportsTypes';
import { getFrontendColorScaleFromBackend } from './Mutations/utils/densityColorMapping';

interface ReportDetailProps {
  className: string;
}

interface ReportDetailsContentProps {
  report: ParsedReportParams | undefined;
  reportTemplate: ReportTemplate;
}

interface ReportDetailItemProps {
  label: string;
  value: string | undefined;
  className?: string;
}

const ReportDetailItem = ({
  label,
  value,
  className,
}: ReportDetailItemProps) => {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : 'Unknown'}
      </p>
    </div>
  );
};

interface ShiplistReportDetailItemProps {
  label: string;
  shiplistId: string;
}

const ShipListReportDetailItem = ({
  label,
  shiplistId,
}: ShiplistReportDetailItemProps) => {
  const {
    data: detailedShiplist,
    isLoading,
    isError,
  } = useGetShiplist(shiplistId);

  // It's not all customers that have access to shiplists, in which case we cannot grab the name
  if (isError) {
    return null;
  }

  if (isLoading) {
    return <LoadingSpinner size={'small'}></LoadingSpinner>;
  }

  if (!detailedShiplist) {
    return null;
  }

  return (
    <ReportDetailItem
      label={label}
      value={detailedShiplist?.name as string}
    ></ReportDetailItem>
  );
};

interface ShipsReportDetailItemProps {
  label: string;
  ships: Ship[];
  className?: string;
}

const ShipsReportDetailItem = ({
  label,
  ships,
  className,
}: ShipsReportDetailItemProps) => {
  const formattedValue = ships.map((ship) => ship.name).join(', ');

  return (
    <ReportDetailItem
      className={className}
      label={label}
      value={formattedValue}
    ></ReportDetailItem>
  );
};

interface ShapeReportDetailItemProps {
  shapeId: string;
}

const ShapeReportDetailItem = ({ shapeId }: ShapeReportDetailItemProps) => {
  const { messages } = useLocalization();

  const { data: shape, isLoading, isError } = useShape(shapeId);

  const shapeName = useMemo(() => {
    if (shape) {
      return shape.properties.name;
    }

    return 'instant or unknown shape';
  }, [shape]);

  if (isLoading) {
    return <LoadingSpinner size="small"></LoadingSpinner>;
  }

  return (
    <ReportDetailItem
      label={messages.reportsDetailsShapeLabel}
      value={shapeName}
    ></ReportDetailItem>
  );
};

const ReportDetailsContent = ({
  report,
  reportTemplate,
}: ReportDetailsContentProps) => {
  const { messages } = useLocalization();

  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <ScrollArea className="flex-grow p-0 overflow-y-auto">
      <div className="space-y-6">
        <div className="grid gap-4">
          <div>
            <h3 className="text-lg font-medium">Basic Details</h3>
            <Separator className="mt-2"></Separator>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <ReportDetailItem
              label={messages.reportsDetailsNameLabel}
              value={report?.info?.['Job name'] ? report?.info['Job name'] : ''}
            ></ReportDetailItem>
            <ReportDetailItem
              label={messages.reportsDetailsIdLabel}
              value={report?.info?.['Job ID'] ? report?.info['Job ID'] : ''}
            ></ReportDetailItem>
            <ReportDetailItem
              label={messages.reportsDetailsTypeLabel}
              value={report?.info?.['Job type'] ? report?.info['Job type'] : ''}
            ></ReportDetailItem>

            <ReportDetailItem
              label={messages.reportsDetailsGeneratedLabel}
              value={formatDateTimeWithTimeZone(report?.info?.['Created'])}
            ></ReportDetailItem>

            <ReportDetailItem
              label={messages.reportsDetailsCreatedByLabel}
              value={report?.info?.['Owner'] ? report?.info['Owner'] : ''}
            ></ReportDetailItem>
            <ReportDetailItem
              label={messages.reportsDetailsStatusLabel}
              value={report?.info?.['Status'] ? report?.info['Status'] : ''}
            ></ReportDetailItem>
            <ReportDetailItem
              label={messages.reportsDetailsDurationLabel}
              value={report?.info?.Duration ? report.info.Duration : ''}
            ></ReportDetailItem>
          </div>
        </div>
        <div className="grid gap-4">
          <div>
            <h3 className="text-lg font-medium">Period</h3>
            <Separator className="mt-2"></Separator>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <ReportDetailItem
              label={messages.reportsDetailsStartTimeLabel}
              value={formatDateTimeWithTimeZone(report?.info?.['Start time'])}
            ></ReportDetailItem>

            <ReportDetailItem
              label={messages.reportsDetailsEndTimeLabel}
              value={formatDateTimeWithTimeZone(report?.info?.['End time'])}
            ></ReportDetailItem>
          </div>
          <div className="grid gap-4">
            <div>
              <h3 className="text-lg font-medium">Shape and Filter</h3>
              <Separator className="mt-2"></Separator>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {report?.info?.['Area'] && (
                <ReportDetailItem
                  label={messages.reportsDetailsShapeLabel}
                  value={report.info['Area']}
                ></ReportDetailItem>
              )}
              {report?.params?.['GAC']?.shape_id && !report?.info?.['Area'] && (
                <ShapeReportDetailItem
                  shapeId={report.params['GAC'].shape_id}
                ></ShapeReportDetailItem>
              )}
              {report?.params?.['departure']?.properties?.name && (
                <ReportDetailItem
                  label={messages.reportsDetailsDepartureLabel}
                  value={report.params['departure'].properties.name}
                ></ReportDetailItem>
              )}
              {report?.params?.['destination']?.properties?.name && (
                <ReportDetailItem
                  label={messages.reportsDetailsArrivalLabel}
                  value={report.params['destination'].properties.name}
                ></ReportDetailItem>
              )}
              {report?.params?.['FN'] && (
                <ReportDetailItem
                  label={messages.reportsDetailsFilterLabel}
                  value={report.params['FN']}
                ></ReportDetailItem>
              )}
              {report?.params?.['TL'] && (
                <ShipsReportDetailItem
                  className="col-span-2"
                  label={messages.reportsDetailsShipsLabel}
                  ships={report.params['TL']}
                ></ShipsReportDetailItem>
              )}
              {report?.params?.['shiplist'] && (
                <ShipListReportDetailItem
                  label={messages.reportsDetailsShiplistLabel}
                  shiplistId={report.params['shiplist']}
                ></ShipListReportDetailItem>
              )}
            </div>
          </div>
          <div className="grid gap-4">
            <div>
              <h3 className="text-lg font-medium">Miscellaneous</h3>
              <Separator className="mt-2"></Separator>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {report?.params?.['CS'] && (
                <ReportDetailItem
                  label="Color scale"
                  value={getFrontendColorScaleFromBackend(report.params['CS'])}
                />
              )}
              {report?.params?.['CB'] &&
                (reportTemplate.type === 'HISTORY_TRACK_TB' ||
                  reportTemplate.type === 'DENSITY_TB') && (
                  <ReportDetailItem
                    label="Color by"
                    value={getSelectLabelByValue(
                      report.params['CB'],
                      reportTypesObject[reportTemplate.type].miscellaneous
                        .colorBy.options as SelectType[]
                    )}
                  />
                )}

              {report?.params?.['CD'] && (
                <ReportDetailItem
                  label="Number of colors"
                  value={`${report.params['CD']}`}
                />
              )}
              {report?.params?.['CPD'] && (
                <ReportDetailItem
                  label="Cell size"
                  value={`${report.params['CPD']}`}
                />
              )}
              {report?.params?.['SMMLMIN'] && (
                <ReportDetailItem
                  label="Lower limit"
                  value={`${report.params['SMMLMIN']}`}
                />
              )}
              {report?.params?.['SMMLMAX'] && (
                <ReportDetailItem
                  label="Upper limit"
                  value={`${report.params['SMMLMAX']}`}
                />
              )}
              {report?.params?.['PAC'] && (
                <ReportDetailItem
                  label="Output type"
                  value={`${report.params['PAC']}`}
                />
              )}
              {report?.params?.['STG'] && (
                <ReportDetailItem
                  label="Statistic grouping"
                  value={`${report.params['STG']}`}
                />
              )}
              {report?.params?.['PI'] && (
                <ReportDetailItem
                  label="Interval"
                  value={`${report.params['PI']}`}
                />
              )}
              {report?.info?.['Watchdogs (1)'] && (
                <ReportDetailItem
                  label={
                    report?.info?.['Watchdogs (2)'] ? 'Watchdog 1' : 'Watchdog'
                  }
                  value={`${report.info['Watchdogs (1)']}`}
                />
              )}
              {report?.info?.['Watchdogs (2)'] && (
                <ReportDetailItem
                  label="Watchdog 2"
                  value={`${report.info['Watchdogs (2)']}`}
                />
              )}
              {report?.info?.['Watchdogs (3)'] && (
                <ReportDetailItem
                  label="Watchdog 3"
                  value={`${report.info['Watchdogs (3)']}`}
                />
              )}
              {report?.info?.['Watchdogs (4)'] && (
                <ReportDetailItem
                  label="Watchdog 4"
                  value={`${report.info['Watchdogs (4)']}`}
                />
              )}
              {report?.info?.['Watchdogs (5)'] && (
                <ReportDetailItem
                  label="Watchdog 5"
                  value={`${report.info['Watchdogs (5)']}`}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

interface ReportDetailSidePanelProps {
  className?: string;
  reportResult?: ReportResult | undefined;
}

const ReportDetailSidePanel: React.FC<ReportDetailSidePanelProps> = ({
  className,
  reportResult,
}) => {
  const { selectedJob: job, selectedReportTemplate: reportTemplate } =
    useReportContext();
  const { messages } = useLocalization();
  const { timeZone } = useTimeZoneContext();
  const { data: report, isLoading } = useGetReport((job as Job).uuid, timeZone);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { setReportDetails, resetReportContext } = useReportContext();

  const {
    centerComponent,
    setCenterComponent,
    setCenterComponentData,
    setTableExpanded,
    setReportDetailsExpanded,
  } = useMapPanelContext();

  useEffect(() => {
    if (report) {
      setReportDetails(report);
    }
  }, [report, setReportDetails]);

  const onExit = useOnMapPanelExit();
  const onBackToReports = () => {
    if (centerComponent === CENTERCOMPONENTS.SHIPTABLE) {
      setCenterComponent(CENTERCOMPONENTS.NONE);
    }
    setCenterComponentData(null);
    resetReportContext();
    setTableExpanded(true);
  };

  return (
    <Card
      className={cn('flex flex-col relative group/report-details', className)}
    >
      <Button
        className="absolute top-20 -left-3 z-900 bg-background rounded-full transform border border-text-gray-900 text-gray-700 hover:bg-secondary h-6 w-6 p-0 invisible group-hover/report-details:visible"
        onClick={() => setReportDetailsExpanded(false)}
      >
        <PiCaretRightLight className="h-4 w-4" />
      </Button>

      <CardHeader>
        <div className="space-y-2">
          <div className="flex justify-between items-center mb-4">
            <Button              
              variant="ghost"
              className="flex items-center pl-2 justify-start w-fit"
              onClick={() => onBackToReports()}
            >
              <PiCaretLeftThin />
              <div className="pl-2">{messages.reportsDetailsBackToReports}</div>              
            </Button>
            <Button              
              variant="ghost"
              size="icon"
              className="-mt-10 -mr-4"
              onClick={() => {
                onBackToReports();
                onExit();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Button>
          </div>
          <CardTitle className="text-lg font-medium text-muted-foreground">
            {messages.reportsDetailsTitle}
          </CardTitle>
          <CardDescription>{messages.reportsDetailsSubtitle}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col overflow-hidden">
        {isLoading && <LoadingSpinner />}
        {!isLoading && (
          <ReportDetailsContent
            report={report}
            reportTemplate={reportTemplate as ReportTemplate}
          />
        )}
      </CardContent>
      <CardFooter className="flex flex-grow-0 items-end min-h-52 justify-between">
        <Button
          onClick={() => setShowDeleteDialog(true)}
          variant="ghost"
          className="text-primary hover:text-primary"
        >
          {messages.genericDelete}
        </Button>
        {showDeleteDialog && (
          <ReportDelete
            showDialog={showDeleteDialog}
            setShowDialog={setShowDeleteDialog}
            job={job as Job}
            onBackToReports={() => onBackToReports()}
          />
        )}
        <ReportDetailActionsMenu
          reportTemplate={reportTemplate as ReportTemplate}
          report={report}
          jobId={(job as Job).uuid}
          exportTypes={reportResult?.exportFormats}
        ></ReportDetailActionsMenu>
      </CardFooter>
    </Card>
  );
};

interface ReportDetailsSidePanelFailedProps {
  className?: string;
}

const ReportDetailsSidePanelFailed = ({
  className,
}: ReportDetailsSidePanelFailedProps) => {
  const { reportDetailsExpanded } = useMapPanelContext();

  if (!reportDetailsExpanded) {
    return null;
  }

  return <ReportDetailSidePanel className={className} />;
};

interface ReportDetailsSidePanelSuccessProps {
  className?: string;
}

const ReportDetailsSidePanelSuccess = ({
  className,
}: ReportDetailsSidePanelSuccessProps) => {
  const { selectedJob: job } = useReportContext();
  const reportResult = useManageReportProcessing((job as Job).uuid, true);
  const { setHasLoaded, setIsWaiting } = useReportContext();

  useEffect(() => {
    if (reportResult) {
      setHasLoaded(true);
      setIsWaiting(false);
    } else {
      setHasLoaded(false);
      setIsWaiting(true);
    }
  }, [reportResult, setHasLoaded, setIsWaiting]);

  const { reportDetailsExpanded } = useMapPanelContext();

  if (!reportDetailsExpanded) {
    return null;
  }

  return (
    <ReportDetailSidePanel
      className={className}
      reportResult={reportResult}
    ></ReportDetailSidePanel>
  );
};

export const ReportDetails = ({ className }: ReportDetailProps) => {
  const { selectedJob } = useReportContext();

  if ((selectedJob as Job).status === 'FAILED') {
    return (
      <ReportDetailsSidePanelFailed
        className={className}
      ></ReportDetailsSidePanelFailed>
    );
  } else {
    return (
      <ReportDetailsSidePanelSuccess
        className={className}
      ></ReportDetailsSidePanelSuccess>
    );
  }
};
